import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import reduxUtil from "../../utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import scenarioService from "../../services/ai/scenarioService";
import { decToPct, pctToDec } from "./scenarioParamUtil";
import { DpCustHrchySelect } from "./AiDpScenarioPlanning";
import {
  Box, Button, Chip, CircularProgress, InputBase, Menu, MenuItem, Modal, Paper, Select, Skeleton,
  TextField, Typography,
} from "@mui/material";
import ArrowBackIcon         from "@mui/icons-material/ArrowBack";
import AddCircleOutlineIcon  from "@mui/icons-material/AddCircleOutline";
import ArrowForwardIcon      from "@mui/icons-material/ArrowForward";
import CheckCircleIcon       from "@mui/icons-material/CheckCircle";
import WarningAmberIcon      from "@mui/icons-material/WarningAmber";
import InboxOutlinedIcon     from "@mui/icons-material/InboxOutlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import DeleteOutlineIcon     from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon      from "@mui/icons-material/EditOutlined";
import StarIcon              from "@mui/icons-material/Star";
import StarBorderIcon        from "@mui/icons-material/StarBorder";
import IconButton            from "@mui/material/IconButton";
import Tooltip               from "@mui/material/Tooltip";
import { C, BTN, modC } from "@plannel/components/ai/aiTokens";
import { FONT } from "@plannel/components/ai/typographyTokens";

// ── 커스텀 시나리오 관리 화면 (P2-A1) ─────────────────────────────────────────
// 전 모듈(BF/DP/IP/RP/MP) 공용 개별 화면 — TabMenuList 에 AI_SCENARIO_MANAGE 로 등록.
// 각 시나리오 플래닝 화면의 "시나리오 관리" 버튼이 viewState(moduleCode·sourceViewName)를
// 넘기고 updateHistoryState 로 이 화면을 연다. 복귀는 sourceViewName(없으면 대상 모듈 플래닝 화면).
// 목록/Context 입력/변환 검토/등록 완료 4뷰. 변환은 POST /scenario/{mod}/custom(Strands),
// 저장/조회/삭제는 z_ai_scenario(source_type='USER') CRUD(custom-save/custom-scenarios).

const MODULE_CODES = ["BF", "DP", "IP", "RP", "MP", "FP"];
const MIN_CONTEXT_LEN = 30;
// AI 변환이 이 시간을 넘기면 "수동 입력으로 전환" 선택지를 노출한다(요청은 계속 진행).
const CONVERT_SLOW_MS = 15000;

// 모듈 코드 → 시나리오 플래닝 화면 reduxKey (TabMenuList AI_SCENARIO_PLANNING 항목과 동일)
const PLANNING_VIEW_KEYS = {
  BF: "AI_BF_SCENARIO_PLANNING",
  DP: "AI_DP_SCENARIO_PLANNING",
  IP: "AI_IP_SCENARIO_PLANNING",
  RP: "AI_RP_SCENARIO_PLANNING",
  MP: "MP_AI_SCENARIO_PLANNING",
  FP: "AI_FP_SCENARIO_PLANNING",
};

// 공용 버튼 sx — 시나리오 플래닝 화면(actionBtnAccSx/actionBtnOutlinedSx)과 동일 규격.
//   화면 통일성: 같은 성격 버튼은 같은 높이(34)·글자 크기(FONT.SUB)·라운드(5px), 색은 BTN 토큰만 사용.
//   채운 버튼은 primary 하나뿐이고 보조 액션은 중성 외곽선으로 강도를 낮춘다.
const ACTION_BTN_H = 34;
const btnBaseSx = {
  height: ACTION_BTN_H, minHeight: ACTION_BTN_H, boxSizing: "border-box",
  px: "14px", borderRadius: "5px", fontSize: FONT.SUB, fontWeight: 500,
  // 라벨 수직 정렬 보정 — 상세 근거는 AiScenarioPlanning.js actionBtnBaseSx 주석 참조.
  lineHeight: "19px", paddingTop: "3.5px", paddingBottom: "4.5px",
  textTransform: "none", boxShadow: "none",
  transition: "background-color .15s ease, border-color .15s ease",
  "& .MuiButton-startIcon": { ml: 0, mr: "7px", transform: "translateY(0.5px)", "& > *": { fontSize: 16 } },
  "& .MuiButton-endIcon": { ml: "7px", mr: 0, transform: "translateY(0.5px)", "& > *": { fontSize: 16 } },
  "&.Mui-focusVisible, &:focus-visible": { outline: `2px solid ${C.acc}`, outlineOffset: "2px" },
  "&.Mui-disabled": { opacity: 0.45 },
};
const btnAccSx = {
  ...btnBaseSx,
  bgcolor: BTN.primaryBg, border: `1px solid ${BTN.primaryBg}`, color: BTN.primaryText,
  "&:hover": { bgcolor: BTN.primaryHoverBg, borderColor: BTN.primaryHoverBg, boxShadow: "none" },
  "&.Mui-disabled": { opacity: 0.45, bgcolor: BTN.primaryBg, borderColor: BTN.primaryBg, color: BTN.primaryText },
};
const btnOutSx = {
  ...btnBaseSx,
  bgcolor: "#FFFFFF", border: `1px solid ${BTN.neutralBorder}`, color: BTN.neutralText,
  "&:hover": { bgcolor: BTN.neutralHoverBg, borderColor: BTN.neutralHoverBorder, boxShadow: "none" },
  "&:active": { bgcolor: BTN.neutralActiveBg },
  "&.Mui-disabled": { opacity: 0.45, bgcolor: "#FFFFFF", borderColor: BTN.neutralBorder, color: BTN.neutralText },
};

// ── 변환 검토 화면 전용 토큰 ────────────────────────────────────────────────
// 핸드오프 제안값. aiTokens 의 inputBorder/rowRule/bgPanel 로 승격하는 건은 저장 팝업 핸드오프와
//   함께 한 번에 처리한다(aiTokens 의 C 는 scenarioStatusUi.js 에 통째로 복제돼 있어 파급이 크다).
const IN_BORDER = "#D7DEE7";   // 입력 테두리
const ROW_RULE  = "#EEF1F5";   // 카드 헤더·표 행 구분선
const BG_PANEL  = "#FAFBFD";   // 표 헤더·액션바 배경
// 1열 접힘 판정은 뷰포트가 아니라 **본문 실제 폭** 기준 — Agent Chat 패널(TabContainer)이 열리면
//   콘텐츠 영역만 좁아지고 뷰포트는 그대로다. AiScenarioPlanning StickyActionBar 와 동일한 이유.
const RV_NARROW = "@container (max-width: 1180px)";
// 파라미터 표 4열 — 라벨 / 값 / 신뢰도 / 삭제. 헤더 행과 데이터 행이 같은 값을 쓴다.
const PARAM_COLS = "232px 1fr 132px 34px";

// 입력 톤 — 기본 / 신뢰도 낮음(직접 확인 필요) / 허용 범위 밖
const TONE = {
  base: { line: IN_BORDER, hover: BTN.neutralHoverBorder, focus: C.acc, ring: `${C.acc}1A` },
  low:  { line: C.g3s,     hover: C.g3,                   focus: C.g3,  ring: `${C.g3}1A`  },
  warn: { line: C.g2s,     hover: C.g2,                   focus: C.g2,  ring: `${C.g2}1F`  },
};

// 입력 공통 스킨 — MUI outlined 기본값(높이·radius·테두리색)을 디자인 값으로 덮는다.
//   저장 팝업(ScenarioSaveCustomDialog fieldBaseSx)과 같은 관용구. 3px 포커스 링은 fieldset 이 아니라
//   root 의 boxShadow 로 — fieldset 에 걸면 아웃라인 박스에 잘린다.
const fieldBaseSx = (tone = "base") => {
  const k = TONE[tone] ?? TONE.base;
  return {
    "& .MuiOutlinedInput-root": {
      fontSize: FONT.BODY, color: C.text1, bgcolor: "#fff", borderRadius: "6px",
      "& fieldset":             { borderColor: k.line },
      "&:hover fieldset":       { borderColor: k.hover },
      "&.Mui-focused fieldset": { borderColor: k.focus, borderWidth: "1px" },
      "&.Mui-focused":          { boxShadow: `0 0 0 3px ${k.ring}` },
    },
    "& .MuiOutlinedInput-input::placeholder": { color: C.text3, opacity: 1 },
  };
};
// 단행 입력 — 높이는 **루트**(.MuiOutlinedInput-root)에 못 박는다. 내부 요소에 걸면 MUI 자체 규칙과
//   특이도가 맞물려 이기고 지는 게 삽입 순서에 좌우된다(Select 의 `&.MuiSelect-select{height:auto}` 사례).
//   34=서술 필드, 30=파라미터 행.
const fieldSx = (tone = "base", h = 34) => {
  const base = fieldBaseSx(tone);
  return {
    ...base,
    "& .MuiOutlinedInput-root": { ...base["& .MuiOutlinedInput-root"], height: h, boxSizing: "border-box" },
    "& .MuiOutlinedInput-input": { height: "100%", boxSizing: "border-box", p: "0 11px" },
  };
};
// textarea — rows(고정) + resize:vertical. minRows(TextareaAutosize)는 인라인 height 를 써서
//   사용자의 리사이즈 드래그와 싸우므로 쓰지 않는다(저장 팝업과 동일 판단).
const areaSx = (tone = "base") => {
  const base = fieldBaseSx(tone);
  return {
    ...base,
    "& .MuiOutlinedInput-root":  { ...base["& .MuiOutlinedInput-root"], p: "9px 11px" },
    "& .MuiOutlinedInput-input": { p: 0, lineHeight: 1.6, resize: "vertical" },
  };
};
// Select — TextField 와 달리 FormControl 래퍼가 없어 sx 가 붙는 **루트 자체가** .MuiOutlinedInput-root 다.
//   따라서 fieldBaseSx 의 자손 셀렉터(`& .MuiOutlinedInput-root`)는 매치되지 않는다(자기 자신 미포함).
//   같은 속성을 최상위에 직접 얹는다. MuiSelect-select 는 min-height 가 하드코딩돼 있어 unset 필요.
//   높이도 루트에 준다 — MUI 가 `&.MuiSelect-select { height: auto; min-height: 1.4375em }` 를 갖고 있어
//   내부 요소에 height 를 걸면 특이도 동률로 밀릴 수 있다(그때 드롭다운만 17px 남짓으로 낮아진다).
const selectSx = (tone = "base", h = 30) => {
  const k = TONE[tone] ?? TONE.base;
  return {
    height: h, boxSizing: "border-box",
    fontSize: FONT.BODY, color: C.text1, bgcolor: "#fff", borderRadius: "6px",
    "& fieldset":             { borderColor: k.line },
    "&:hover fieldset":       { borderColor: k.hover },
    "&.Mui-focused fieldset": { borderColor: k.focus, borderWidth: "1px" },
    "&.Mui-focused":          { boxShadow: `0 0 0 3px ${k.ring}` },
    "& .MuiSelect-select": {
      minHeight: "unset", boxSizing: "border-box",
      display: "flex", alignItems: "center", p: "0 32px 0 10px",
    },
  };
};
// DpCustHrchySelect 의 inputSx 는 트리거 div 와 팝오버 검색 input 양쪽에 spread 되므로
//   **순수 CSS 속성만** 넣는다(& 셀렉터·의사클래스 객체 금지). border 는 shorthand 유지 —
//   컴포넌트 자체의 포커스 borderColor 오버라이드가 소스 순서로 이겨야 한다.
const hrchyInputSx = (h = 30) => ({
  width: "100%", height: h, boxSizing: "border-box", p: "0 10px",
  border: `1px solid ${IN_BORDER}`, borderRadius: "6px",
  fontSize: FONT.BODY, bgcolor: "#fff",
});

// 신뢰도 집계 — 빈 값(수동 입력·수정 모드)은 세지 않는다.
function countConfidences(list) {
  const acc = { high: 0, mid: 0, low: 0 };
  (list || []).forEach(v => { if (acc[v] != null) acc[v] += 1; });
  return acc;
}
// 범위 초과 시 서버가 접을 예정값 — 저장값(소수) 단위. 표시 단위 변환은 호출부에서.
function clampPreview(row, num) {
  return (row.min != null && num < row.min) ? row.min : row.max;
}

// 필드 라벨 (필수표시·우측 배지 옵션)
function FieldLabel({ children, required = false, badge = null }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.5 }}>
      {/* component="div" — App.scss 의 `@media(max-width:1400px) p.MuiTypography-body1 !important` 가
          기본 <p> 렌더를 14px 로 덮어써 FONT.SUB(13) 지정이 무시되는 것을 피한다. */}
      <Typography component="div" sx={{ fontSize: FONT.SUB, fontWeight: 600, color: C.text2 }}>
        {children}{required && <Box component="span" sx={{ color: C.g3, ml: 0.3 }}>*</Box>}
      </Typography>
      {badge}
    </Box>
  );
}

// confidence 배지 — 변환 결과 필드별 신뢰도(높음/중간/낮음)
//   short: 폭이 좁은 자리(파라미터 표 신뢰도 열)에서 "높음/중간/낮음" 축약 라벨을 쓴다.
//   기본(긴) 라벨 키 confHigh/confMid/confLow 는 Agent Chat 초안 카드와 공유하므로 문구를 바꾸지 않는다.
function ConfBadge({ level, short = false }) {
  const { t } = useTranslation();
  const cfg = {
    high: { label: t(short ? "confHighShort" : "confHigh"), bg: C.g1l, color: C.g1t },
    mid:  { label: t(short ? "confMidShort"  : "confMid"),  bg: C.g2l, color: C.g2t },
    low:  { label: t(short ? "confLowShort"  : "confLow"),  bg: C.g3l, color: C.g3t },
  }[level];
  if (!cfg) return null;
  return (
    <Chip label={cfg.label} size="small"
      sx={{ bgcolor: cfg.bg, color: cfg.color, fontSize: FONT.MICRO, fontWeight: 700,
            height: 18, "& .MuiChip-label": { px: "7px" } }} />
  );
}

// 섹션 머리 — 라벨(+개수 배지) 우측을 1px 하드라인이 채운다. 검토 화면 우열에서만 사용.
function SectionHead({ label, count = null }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography component="div" sx={{ fontSize: FONT.CAPTION, fontWeight: 700,
                                        letterSpacing: ".06em", color: C.text2 }}>
        {label}
      </Typography>
      {count != null && (
        <Box sx={{ height: 18, px: "7px", borderRadius: "9px", bgcolor: C.accL, color: C.accT,
                   fontSize: FONT.CAPTION, fontWeight: 700, fontVariantNumeric: "tabular-nums",
                   display: "flex", alignItems: "center" }}>{count}</Box>
      )}
      <Box sx={{ flex: 1, height: "1px", bgcolor: C.border }} />
    </Box>
  );
}

// 추출 신뢰도 카드의 3분할 카운트 타일
function ConfTile({ n, label, bg, color }) {
  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "3px",
               p: "8px 10px", borderRadius: "6px", bgcolor: bg, color }}>
      <Box sx={{ fontSize: FONT.H1, fontWeight: 700, fontVariantNumeric: "tabular-nums", lineHeight: 1.1 }}>
        {n}
      </Box>
      <Box sx={{ fontSize: FONT.CAPTION, lineHeight: 1.2 }}>{label}</Box>
    </Box>
  );
}

// 파라미터 추가 — 점선 버튼 + 후보 메뉴.
//   anchorEl 을 자체 보유하므로 화면 함수 밖(모듈 스코프)에 있어야 한다. 안에 두면 매 렌더 identity 가
//   바뀌어 리마운트되고, 열려 있던 메뉴가 입력 한 글자마다 닫힌다.
function AddParamButton({ candidates, onPick }) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  if (!candidates.length) return null;
  return (
    <>
      <Box component="button" type="button" onClick={e => setAnchorEl(e.currentTarget)}
        sx={{ alignSelf: "flex-start", display: "flex", alignItems: "center", gap: "6px",
              height: 30, px: "12px", border: `1px dashed ${BTN.neutralBorder}`, borderRadius: "6px",
              bgcolor: "transparent", color: BTN.neutralText, fontSize: FONT.BODY,
              fontFamily: "inherit", cursor: "pointer",
              transition: "background-color .15s ease, border-color .15s ease, color .15s ease",
              "&:hover": { bgcolor: BTN.neutralHoverBg, borderColor: BTN.neutralHoverBorder, color: C.acc },
              "&:focus-visible": { outline: `2px solid ${C.acc}`, outlineOffset: "2px" } }}>
        <AddCircleOutlineIcon sx={{ fontSize: 13 }} />
        {t("addParamBtn")}
      </Box>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
        {candidates.map(c => (
          <MenuItem key={c.key} sx={{ fontSize: FONT.BODY }}
            onClick={() => { setAnchorEl(null); onPick(c); }}>
            {t(c.label_key || c.label) || c.key}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

// ── 근거(evidence) 하이라이트 ────────────────────────────────────────────────
// 백엔드가 필드별로 "상황 설명 원문에서 그대로 복사한 구절"(evidence)을 함께 돌려준다.
// 신뢰도 배지가 '높음'인 필드는 그 구절이 원문에 실제로 존재한다는 뜻이므로, 원문 위에 표시해
// "AI가 무엇을 근거로 이 값을 뽑았는지"를 사용자가 즉시 확인할 수 있게 한다.

// 공백을 무시한 부분 문자열 검색 — LLM 이 띄어쓰기를 다르게 옮겨도 원문 위치를 찾는다.
//   반환: [원문 시작 index, 원문 끝 index) 또는 null.
function findLooseRange(src, needle) {
  const text = String(src || "");
  const map = [];          // 정규화 문자열의 각 문자 → 원문 index
  let norm = "";
  for (let i = 0; i < text.length; i++) {
    if (/\s/.test(text[i])) continue;
    norm += text[i].toLowerCase();
    map.push(i);
  }
  const n = String(needle || "").replace(/\s+/g, "").toLowerCase();
  if (!n) return null;
  const at = norm.indexOf(n);
  if (at < 0) return null;
  return [map[at], map[at + n.length - 1] + 1];
}

// 원문 + 근거 구절 목록 → 하이라이트 렌더. active 와 일치하는 구절은 진하게(포커스 연동).
function EvidenceText({ text, evidences, active }) {
  const src = String(text || "");
  const raw = [];
  (evidences || []).forEach(ev => {
    const r = findLooseRange(src, ev);
    if (r) raw.push({ start: r[0], end: r[1], hot: !!active && ev === active });
  });
  raw.sort((a, b) => a.start - b.start || a.end - b.end);
  // 겹치는 구간 병합 — 여러 필드가 같은 문장을 근거로 삼으면 한 덩어리로 칠한다.
  const ranges = [];
  raw.forEach(r => {
    const last = ranges[ranges.length - 1];
    if (last && r.start <= last.end) {
      last.end = Math.max(last.end, r.end);
      last.hot = last.hot || r.hot;
    } else {
      ranges.push({ ...r });
    }
  });
  const parts = [];
  let cur = 0;
  ranges.forEach((r, i) => {
    if (r.start > cur) parts.push(<span key={`p${i}`}>{src.slice(cur, r.start)}</span>);
    parts.push(
      <Box component="span" key={`h${i}`} sx={{
        bgcolor: r.hot ? C.accS : C.accL, color: C.accT,
        borderRadius: "3px", px: "2px", transition: "background-color .15s",
      }}>{src.slice(r.start, r.end)}</Box>
    );
    cur = r.end;
  });
  if (cur < src.length) parts.push(<span key="tail">{src.slice(cur)}</span>);
  return <>{parts}</>;
}

// 등록 위저드 스텝 인디케이터 (① Context 입력 → ② 변환 검토 → ③ 등록 완료)
function StepIndicator({ current }) {
  const { t } = useTranslation();
  const steps = [t("regStep1Context"), t("regStep2Review"), t("regStep3Done")];
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
      {steps.map((label, i) => (
        <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {i > 0 && <ArrowForwardIcon sx={{ fontSize: 12, color: C.text3 }} />}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
            <Box sx={{
              width: 17, height: 17, borderRadius: "50%",
              bgcolor: i === current ? C.acc : C.bg3,
              color: i === current ? "#fff" : C.text3,
              fontSize: FONT.MICRO, fontWeight: 700,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>{i + 1}</Box>
            <Typography sx={{
              fontSize: FONT.SUB, fontWeight: i === current ? 700 : 500,
              color: i === current ? C.acc : C.text3,
            }}>{label}</Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// 모듈 선택 칩 행 (단일 선택). allLabel 지정 시 "전체" 칩을 맨 앞에 노출(목록 필터용).
function ModuleChips({ value, onChange, allLabel = null }) {
  const baseSx = (on) => ({
    fontSize: FONT.CAPTION, fontWeight: on ? 700 : 500, height: 24, cursor: "pointer",
    "& .MuiChip-label": { px: "10px" },
  });
  // "전체" 칩 — 특정 모듈이 없으므로 기존 파란 액센트를 그대로 쓴다(도트 없음).
  const allSx = (on) => ({
    ...baseSx(on),
    bgcolor: on ? C.accL : C.bg2, color: on ? C.accT : C.text2,
    border: `1px solid ${on ? C.accS : C.border}`,
    "&:hover": { bgcolor: on ? C.accL : C.bg3 },
  });
  const modSx = (m, on) => ({
    ...baseSx(on),
    bgcolor: on ? m.bg : C.bg2, color: on ? m.text : "#6b7688",
    border: `1px solid ${on ? m.bar : m.border}`,
    "&:hover": { bgcolor: on ? m.bg : C.bg3 },
  });
  // 라벨 왼쪽 모듈 색 도트 — 비활성 상태에서도 어느 모듈인지 색으로 읽히게 한다.
  const modLabel = (code, m, on) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
      <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: m.bar, opacity: on ? 1 : 0.85 }} />
      {code}
    </Box>
  );
  return (
    <Box sx={{ display: "flex", gap: 0.8, flexWrap: "wrap" }}>
      {allLabel != null && (
        <Chip label={allLabel} size="small" onClick={() => onChange(null)} sx={allSx(value == null)} />
      )}
      {MODULE_CODES.map(code => {
        const m = modC(code);
        const on = value === code;
        return (
          <Chip key={code} label={modLabel(code, m, on)} size="small"
            onClick={() => onChange(code)} sx={modSx(m, on)} />
        );
      })}
    </Box>
  );
}

// ── 메인 화면 ────────────────────────────────────────────────────────────────
export default function ScenarioManageScreen({ viewName }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isPermitted } = useMenuAccess();
  // 진입 컨텍스트 — 출처 플래닝 화면이 viewState 로 주입(메뉴 직접 진입 시 없음)
  const saved = reduxUtil.getViewState(viewName);
  const entryModule = saved?.moduleCode ?? null;
  const sourceViewName = saved?.sourceViewName ?? null;
  // 작업상태 복원 — 탭 왕복(리마운트) 시 마지막 작업을 유지.
  const w = saved?.csmWork ?? null;
  const restored = !!w;
  // 버튼 진입(csmEntry): 목록 뷰로(해당 모듈 필터), 위저드 폼 초기화. 단 목록·업종 캐시(w)는 유지(재조회 X).
  const entryReset = !!saved?.csmEntry;
  // 화면 전환 state machine: list(목록) → context(입력) → review(검토) → done(완료) → list
  const [view, setView] = useState(entryReset ? "list" : (w?.view ?? "list"));
  // 목록 모듈 필터 (null = 전체) — 버튼 진입 시 해당 모듈만 우선 표시
  const [listFilter, setListFilter] = useState(entryReset ? (entryModule ?? null) : (w?.listFilter ?? null));
  // 등록 위저드 폼 — Context 입력
  const [targetModule, setTargetModule] = useState(
    entryReset ? (entryModule ?? MODULE_CODES[0]) : (w?.targetModule ?? entryModule ?? MODULE_CODES[0]));
  const [contextText, setContextText] = useState(entryReset ? "" : (w?.contextText ?? ""));
  // 참고 시나리오 — 대상 모듈의 표준 시나리오 카탈로그(이름·설명) 드롭다운
  const [refScenarios, setRefScenarios] = useState(w?.refScenarios ?? []);
  const [refScenarioCd, setRefScenarioCd] = useState(w?.refScenarioCd ?? "");
  const [refLoading, setRefLoading] = useState(!Array.isArray(w?.refScenarios));
  // 변환(AI 생성) 상태
  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState("");
  // 변환 검토 필드 — AI 생성 결과로 채운 뒤 사용자가 수정. conf* = 필드별 신뢰도(high/mid/low).
  // 위저드 검토 폼 — 버튼 진입 시엔 초기화(빈 값), 탭 복원 시엔 복원값
  const ww = entryReset ? null : w;
  const [scnName, setScnName]       = useState(ww?.scnName ?? "");
  const [confName, setConfName]     = useState(ww?.confName ?? "");
  const [direction, setDirection]   = useState(ww?.direction ?? "");
  const [confDirection, setConfDirection] = useState(ww?.confDirection ?? "");
  const [descTxt, setDescTxt]       = useState(ww?.descTxt ?? "");
  const [confDesc, setConfDesc]     = useState(ww?.confDesc ?? "");
  const [issueSituation, setIssueSituation] = useState(ww?.issueSituation ?? "");
  const [confIssue, setConfIssue]   = useState(ww?.confIssue ?? "");
  const [variantDesc, setVariantDesc] = useState(ww?.variantDesc ?? "");
  const [confVariant, setConfVariant] = useState(ww?.confVariant ?? "");
  const [triggerKeywords, setTriggerKeywords] = useState(ww?.triggerKeywords ?? []); // string[]
  const [confTrigger, setConfTrigger] = useState(ww?.confTrigger ?? "");
  const [kwInput, setKwInput] = useState(""); // 트리거 키워드 추가 입력(전이 상태 — 미persist)
  const [paramRows, setParamRows]   = useState(ww?.paramRows ?? []); // [{key, value, confidence}]
  // 검토 대상 모듈의 전체 편집 가능 파라미터 카탈로그 — 사용자가 파라미터를 추가할 때 후보 목록
  const [paramCatalog, setParamCatalog] = useState(ww?.paramCatalog ?? []);
  // 적용 범위(scope_type) — 모듈별 선택지(scopeOptions)에서 선택. 파라미터 변경을 어디에 적용할지.
  const [scopeType, setScopeType] = useState(ww?.scopeType ?? "all");
  const [scopeOptions, setScopeOptions] = useState(ww?.scopeOptions ?? [{ value: "all", label: "전체", label_key: "all" }]);
  // 삭제 확인 모달 대상(행 데이터)
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // 커스텀 시나리오 목록 (z_ai_scenario source_type='USER')
  const [customList, setCustomList] = useState(w?.customList ?? []);
  // 표시할 행이 없으면(신규 진입·빈 복원) 스켈레톤. 복원된 행이 있을 때만 즉시표시+백그라운드 갱신.
  const [customLoading, setCustomLoading] = useState(!(w?.customList?.length));
  // 즐겨찾기 scenario_cd 집합 — 별표 표시용. 목록 정렬에는 쓰지 않는다(정렬은 시나리오 구성 탭 한정).
  const [favCds, setFavCds] = useState(() => new Set(w?.favCds ?? []));
  // 모듈별 파라미터 카탈로그 메타 — 수정(✎) 진입 시 params 코드키를 라벨·옵션으로 복원
  const [customCatalogs, setCustomCatalogs] = useState(w?.customCatalogs ?? {});
  // 모듈별 적용 범위(scope_type) 선택지 — 수정 진입 시 scope 드롭다운 복원
  const [customScopeOptions, setCustomScopeOptions] = useState(w?.customScopeOptions ?? {});
  // 저장(확정 & 즉시 등록) 상태
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [savedCd, setSavedCd] = useState(ww?.savedCd ?? "");
  // 수정 모드 — 목록 ✎ 로 진입한 기존 시나리오 코드 (빈 값 = 신규 등록)
  const [editingCd, setEditingCd] = useState(ww?.editingCd ?? "");
  // 근거(evidence) — 필드별로 AI가 인용한 상황 설명 원문 구절. 검토 화면에서 원문 위에 하이라이트.
  const [evidences, setEvidences] = useState(ww?.evidences ?? {});
  // 하이라이트 대상 원문 — 변환 시점의 상황 설명 스냅샷(이후 입력이 바뀌어도 근거 위치가 어긋나지 않게).
  const [srcText, setSrcText] = useState(ww?.srcText ?? "");
  // 포커스 중인 필드의 근거 구절 — 해당 구절만 진하게(전이 상태, 미persist)
  const [activeEvidence, setActiveEvidence] = useState("");
  // 저장 시 서버가 min/max 범위로 접은 파라미터 목록 — 완료 화면에 보정 사실 안내
  const [saveWarnings, setSaveWarnings] = useState(ww?.saveWarnings ?? []);
  // 변환 지연 Fallback — 15초 초과 시 수동 입력 전환 제안 / 전환 후 응답 도착분 보관
  const [convertSlow, setConvertSlow] = useState(false);            // 전이 상태(미persist)
  const [manualMode, setManualMode] = useState(ww?.manualMode ?? false);
  const [pendingAi, setPendingAi] = useState(ww?.pendingAi ?? null);
  // "낮음 항목만 보기" 필터 — 전이 상태(미persist). csmWork 에 넣지 않는다:
  //   탭 복원 시 필터가 켜진 채 되살아나면 값이 사라진 것처럼 보인다.
  const [lowOnly, setLowOnly] = useState(false);
  // 언마운트 가드 — 변환 요청(최대 120s) 도중 화면 이탈 시 stale setState 방지
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);
  // 변환 세대 토큰 — 수동 전환 후 재변환 등으로 요청이 겹칠 때 이전 응답을 폐기
  const convertSeqRef = useRef(0);
  // 콜백(요청 시작 시점 클로저) 안에서 최신 manualMode 를 읽기 위한 미러
  const manualModeRef = useRef(manualMode);
  useEffect(() => { manualModeRef.current = manualMode; }, [manualMode]);
  // 마지막 persist 스냅샷 — 화면 이탈 뒤 응답이 도착했을 때 viewState 에 직접 격납하기 위함
  const lastWorkRef = useRef(null);
  // 탭 복원(entryReset 아님)일 때만 entryModule 동기화 스킵(복원 targetModule 유지).
  //   버튼 진입(entryReset)은 targetModule 을 entryModule 로 맞추므로 스킵하지 않는다.
  const skipEntrySync = useRef(restored && !entryReset);
  // 참고 시나리오 효과 첫 실행 여부 — 마운트 복원 시에만 조용히 갱신, 이후 모듈 변경은 스켈레톤 표시.
  const refFirstRun = useRef(true);

  // 버튼 진입 플래그 소비 — 이후 탭 왕복은 정상 복원 경로를 타도록 csmEntry 를 끈다.
  useEffect(() => {
    if (entryReset) dispatch(reduxUtil.updateViewState({ viewName, csmEntry: false }));
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // 작업상태 persist — 탭 왕복(리마운트) 복원용. 세션 유지(viewState), 새로고침 초기화.
  useEffect(() => {
    const work = {
      view, listFilter, targetModule, contextText, editingCd, savedCd,
      refScenarios, refScenarioCd,
      scnName, confName, direction, confDirection, descTxt, confDesc,
      issueSituation, confIssue, variantDesc, confVariant, triggerKeywords, confTrigger,
      paramRows, paramCatalog,
      scopeType, scopeOptions, customList, customCatalogs, customScopeOptions,
      evidences, srcText, saveWarnings, manualMode, pendingAi,
      favCds: [...favCds],   // Set 은 직렬화 불가 → 배열로 격납(복원 시 다시 Set)
    };
    lastWorkRef.current = work;
    dispatch(reduxUtil.updateViewState({ viewName, csmWork: work }));
  }, [dispatch, viewName, view, listFilter, targetModule, contextText, editingCd, savedCd,
      refScenarios, refScenarioCd,
      scnName, confName, direction, confDirection, descTxt, confDesc,
      issueSituation, confIssue, variantDesc, confVariant, triggerKeywords, confTrigger,
      paramRows, paramCatalog,
      scopeType, scopeOptions, customList, customCatalogs, customScopeOptions,
      evidences, srcText, saveWarnings, manualMode, pendingAi, favCds]);

  // 커스텀 시나리오 목록 조회 (전 모듈 — 목록 뷰 모듈 필터는 클라이언트 측).
  //   silent=true(복원 후 백그라운드 갱신)면 스켈레톤 없이 조용히 갱신한다.
  const loadCustomList = (silent = false) => {
    if (!silent) setCustomLoading(true);
    scenarioService.listCustomScenarios("all")
      .then(data => {
        if (!mountedRef.current) return;
        setCustomList(data?.scenarios ?? []);
        setCustomCatalogs(data?.param_catalogs ?? {});
        setCustomScopeOptions(data?.scope_options ?? {});
      })
      .catch(() => { /* 조회 실패 시 기존(복원) 목록 유지 */ })
      .finally(() => { if (mountedRef.current) setCustomLoading(false); });
  };
  // 항상 최신 조회. 복원된 행이 있을 때만 조용히(스켈레톤 없이) 갱신, 그 외엔 스켈레톤 표시.
  useEffect(() => { loadCustomList(!!(w?.customList?.length)); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // 즐겨찾기 목록(전 모듈) — 실패 시 복원값 유지, 별표만 비어 보인다(목록 자체는 막지 않는다).
  useEffect(() => {
    scenarioService.listFavoriteScenarios("all")
      .then(cds => { if (mountedRef.current) setFavCds(new Set(cds)); })
      .catch(() => { /* 조회 실패 시 기존(복원) 즐겨찾기 유지 */ });
  }, []);

  // 즐겨찾기 토글 — 낙관적 갱신 후 저장, 실패 시 prev 기준으로 되돌린다.
  const toggleFavorite = (row) => {
    const cd = row?.scenario_cd;
    if (!cd) return;
    const next = !favCds.has(cd);
    setFavCds(prev => {
      const s = new Set(prev);
      if (next) s.add(cd); else s.delete(cd);
      return s;
    });
    scenarioService.setFavoriteScenario(row.module_cd, cd, next)
      .catch(err => {
        console.warn("[ScenarioManage] 즐겨찾기 저장 실패:", err);
        if (!mountedRef.current) return;
        setFavCds(prev => {
          const s = new Set(prev);
          if (next) s.delete(cd); else s.add(cd);
          return s;
        });
      });
  };

  // 다른 모듈 화면에서 재진입 시(마운트 유지 가능성) 진입 모듈로 기본값 재동기화
  //   단, 작업상태 복원 시엔 복원된 targetModule 을 유지(entryModule 로 덮어쓰지 않음).
  useEffect(() => {
    if (skipEntrySync.current) { skipEntrySync.current = false; return; }
    if (entryModule) setTargetModule(entryModule);
  }, [entryModule, sourceViewName]);

  // 참고 시나리오 — 대상 모듈의 표준 시나리오 카탈로그 조회(모듈 바뀌면 재조회·선택 초기화).
  //   RP 는 baseline 포함 전용 엔드포인트, 그 외(BF/DP/IP/MP)는 제네릭. 둘 다 .scenarios 반환.
  //   복원된 목록이 있으면(같은 모듈) 스켈레톤·선택 초기화 없이 백그라운드 갱신.
  useEffect(() => {
    let alive = true;
    // 첫 실행(마운트)이고 복원된 목록이 있을 때만 조용히 백그라운드 갱신.
    //   이후 사용자가 모듈을 바꾸면 스켈레톤을 띄운다(로딩 인지).
    const quiet = refFirstRun.current && refScenarios.length > 0;
    refFirstRun.current = false;
    if (!quiet) { setRefScenarios([]); setRefScenarioCd(""); setRefLoading(true); }
    const req = targetModule === "RP"
      ? scenarioService.getRpScenarios()
      : scenarioService.getScenarios(targetModule);
    req.then(data => {
      if (!alive) return;
      const list = (data?.scenarios ?? [])
        // 표준 시나리오만 — USER 시나리오를 후보에 두면 편집 중인 자기 자신이 참고 대상으로 뜬다.
        .filter(s => (s.source_type ?? "STANDARD") !== "USER")
        // name/desc(원문)는 LLM 프롬프트로 그대로 넘어가므로 보존하고, 화면 표시만 *Key 로 한다.
        .map(s => ({ cd: String(s.scenario_cd ?? ""), name: s.scenario_name ?? "", desc: s.desc_txt ?? "",
                     nameKey: s.scenario_name_key || "", descKey: s.desc_txt_key || "" }))
        .filter(s => s.cd && s.name);
      setRefScenarios(list);
      // 선택값이 새 목록에 없으면 해제(모듈 변경 등)
      setRefScenarioCd(prev => (prev && list.some(s => s.cd === prev)) ? prev : "");
    }).catch(() => { /* 실패 시 기존(복원) 목록 유지 */ })
      .finally(() => { if (alive) setRefLoading(false); });
    return () => { alive = false; };
    // targetModule 변경 시에만 재조회. refScenarios.length 는 실행 시점 스냅샷만 참조하므로 deps 제외.
  }, [targetModule]);  // eslint-disable-line react-hooks/exhaustive-deps

  // 표시는 i18n 키 우선 — 플래닝 화면(scenario_name_key || scenario_name)과 같은 규칙.
  const refName = s => t(s.nameKey || s.name);
  const refDesc = s => t(s.descKey || s.desc);

  // 복귀 — 출처 플래닝 화면으로 탭 전환(출처 없으면 뒤로가기 버튼 자체가 숨겨짐)
  const goBack = () => {
    if (sourceViewName) dispatch(reduxUtil.updateHistoryState(sourceViewName));
  };
  // 복귀·구성 이동 버튼은 대상 화면이 이 테넌트/사용자 권한 안일 때만 내보낸다.
  const canGoBack = !!sourceViewName && isPermitted(sourceViewName);
  const composeTarget = PLANNING_VIEW_KEYS[targetModule] ?? sourceViewName;
  const canGoCompose = !!composeTarget && isPermitted(composeTarget);
  // "시나리오 구성에서 바로 사용하기" — 등록한 대상 모듈의 플래닝 화면으로 이동.
  //   방금 등록한 시나리오를 구성 카드에 자동 추가하고 강조하도록 csmApplyScenario 를 넘긴다
  //   (플래닝 화면이 마운트 시 1회 소비 → apply_custom_saved 액션 실행).
  const goCompose = () => {
    const target = composeTarget;
    if (!target) return;
    dispatch(reduxUtil.updateViewState({
      viewName: target,
      csmApplyScenario: {
        scenario_cd: savedCd,
        module_cd:   targetModule,
        name:        scnName.trim(),
        desc:        descTxt.trim(),
        variant_desc: variantDesc.trim(),
        scope_type:  scopeType,
        params: Object.fromEntries(
          paramRows.filter(r => r.value !== "" && r.value != null
            && !(Array.isArray(r.value) && r.value.length === 0)).map(r => [r.key, r.value])),
      },
    }));
    dispatch(reduxUtil.updateHistoryState(target));
  };

  // 위저드 폼 전체 초기화 — 신규 등록 시작·수정 진입 공통(이전 작업의 근거·보정·지연 상태 잔류 방지)
  const resetDraftMeta = () => {
    setEvidences({}); setSrcText(""); setActiveEvidence("");
    setSaveWarnings([]); setConvertSlow(false); setManualMode(false); setPendingAi(null);
  };

  const startRegister = () => {
    setTargetModule(entryModule ?? MODULE_CODES[0]);
    setContextText(""); setConvertError(""); setEditingCd(""); setSaveError("");
    resetDraftMeta();
    setView("context");
  };

  // 목록 ✎ — 기존 저장값을 검토 화면에 프리필해 수정 모드로 진입 (Context 재입력 불필요)
  const handleEdit = (row) => {
    setEditingCd(row.scenario_cd);
    setTargetModule(row.module_cd || MODULE_CODES[0]);
    // 저장본엔 근거·신뢰도가 없다(원문 자체를 보관하지 않음) → 하이라이트·보정 안내 전부 초기화.
    resetDraftMeta();
    // 저장본엔 신뢰도가 없으므로 conf* 는 전부 비움(배지 미표시).
    setScnName(row.scenario_name ?? ""); setConfName("");
    setDirection(row.scenario_direction ?? ""); setConfDirection("");
    setDescTxt(row.desc_txt ?? ""); setConfDesc("");
    setIssueSituation(row.issue_situation ?? ""); setConfIssue("");
    setVariantDesc(row.variant_desc ?? ""); setConfVariant("");
    setTriggerKeywords(Array.isArray(row.trigger_keywords) ? row.trigger_keywords : []);
    setConfTrigger("");
    // params dict → 편집 행. 라벨·단위·select 옵션은 목록 응답의 모듈 카탈로그에서 복원
    const params = row.params && typeof row.params === "object" ? row.params : {};
    const catalog = customCatalogs[row.module_cd] ?? [];
    setParamRows(Object.entries(params).map(([key, value]) => {
      const m = catalog.find(c => c.key === key) ?? {};
      return {
        key, value: value ?? "", confidence: "", evidence: "",
        label: m.label ?? "", label_key: m.label_key ?? null,
        unit: m.unit ?? "", unit_key: m.unit_key ?? null,
        type: m.type ?? "text", options: Array.isArray(m.options) ? m.options : null, multi: m.multi,
        min: m.min ?? null, max: m.max ?? null,
        percent: m.display === "percent",   // 소수↔% 표시 변환 대상
      };
    }));
    // 적용 범위 — 저장값 복원 + 모듈 scope 선택지(목록 응답 메타)
    const scOpts = customScopeOptions[row.module_cd];
    setScopeType(row.scope_type ?? "all");
    setScopeOptions(Array.isArray(scOpts) && scOpts.length ? scOpts : [{ value: "all", label: "전체", label_key: "all" }]);
    // 파라미터 추가 후보 — 목록 응답의 모듈 카탈로그
    setParamCatalog(catalog);
    setSaveError("");
    setView("review");
  };

  const contextOk = contextText.trim().length >= MIN_CONTEXT_LEN;

  // 트리거 키워드 추가/삭제
  const addKeyword = () => {
    const v = kwInput.trim();
    setKwInput("");
    if (!v || triggerKeywords.includes(v)) return;
    setTriggerKeywords([...triggerKeywords, v]);
  };
  const removeKeyword = (i) => setTriggerKeywords(triggerKeywords.filter((_, idx) => idx !== i));

  // AI 변환 결과를 검토 폼 state 에 반영
  const applyAiResult = (r) => {
    const cv = (n) => (n && typeof n === "object" ? n : { value: n, confidence: "" });
    const nm = cv(r?.scenario_name);
    setScnName(nm.value ?? ""); setConfName(nm.confidence ?? "");
    const dir = cv(r?.scenario_direction);
    setDirection(dir.value ?? ""); setConfDirection(dir.confidence ?? "");
    const ds = cv(r?.desc_txt);
    setDescTxt(ds.value ?? ""); setConfDesc(ds.confidence ?? "");
    const iss = cv(r?.issue_situation);
    setIssueSituation(iss.value ?? ""); setConfIssue(iss.confidence ?? "");
    const vd = cv(r?.variant_desc);
    setVariantDesc(vd.value ?? ""); setConfVariant(vd.confidence ?? "");
    const tk = cv(r?.trigger_keywords);
    setTriggerKeywords(Array.isArray(tk.value) ? tk.value : (tk.value ? [tk.value] : []));
    setConfTrigger(tk.confidence ?? "");
    // 근거 구절 — 필드별로 상황 설명 원문에서 인용한 부분(신뢰도 판정의 실제 근거)
    setEvidences({
      name: nm.evidence ?? "", direction: dir.evidence ?? "", desc: ds.evidence ?? "",
      issue: iss.evidence ?? "", variant: vd.evidence ?? "", trigger: tk.evidence ?? "",
    });
    setSrcText(r?.situation ?? contextText.trim());
    setActiveEvidence("");
    const params = r?.params && typeof r.params === "object" ? r.params : {};
    setParamRows(Object.entries(params).map(([key, v]) => {
      const node = cv(v);
      // 범위(min/max)는 param 노드에 없고 모듈 카탈로그에만 있다 → 카탈로그에서 끌어온다.
      const meta = (Array.isArray(r?.param_catalog) ? r.param_catalog : []).find(c => c?.key === key) ?? {};
      return {
        key, value: node.value ?? "", confidence: node.confidence ?? "",
        evidence: node.evidence ?? "",
        // 표시 메타(백엔드 카탈로그) — 코드 대신 사용자 친화 라벨·단위·select 옵션 렌더용
        label: node.label ?? "", label_key: node.label_key ?? null,
        unit: node.unit ?? "", unit_key: node.unit_key ?? null,
        type: node.type ?? "text", options: Array.isArray(node.options) ? node.options : null, multi: node.multi,
        min: meta.min ?? null, max: meta.max ?? null,
        percent: node.display === "percent",   // 소수↔% 표시 변환 대상
      };
    }));
    // 적용 범위 — LLM 판정값 + 모듈 선택지
    setScopeType(r?.scope_type ?? "all");
    setScopeOptions(Array.isArray(r?.scope_options) && r.scope_options.length
      ? r.scope_options : [{ value: "all", label: "전체", label_key: "all" }]);
    // 모듈 전체 편집 가능 카탈로그 — 파라미터 추가 후보
    setParamCatalog(Array.isArray(r?.param_catalog) ? r.param_catalog : []);
  };

  // "다음 · 변환 시작" — 커스텀 시나리오 AI 생성 호출 후 검토 화면으로.
  //   변환은 LLM 단발 호출(최대 120초)이라 체감상 오래 걸릴 수 있다. CONVERT_SLOW_MS 를 넘기면
  //   "수동 입력으로 전환" 선택지를 띄우고, 전환 뒤에도 요청은 살려둔다(취소하지 않는다) —
  //   응답이 도착하면 사용자가 '내 입력 유지 / AI 결과 사용' 중에 고른다.
  const handleConvert = () => {
    if (!contextOk || converting) return;
    const seq = ++convertSeqRef.current;
    setConverting(true); setConvertError("");
    setConvertSlow(false); setManualMode(false); setPendingAi(null);
    const slowTimer = setTimeout(() => {
      if (mountedRef.current && seq === convertSeqRef.current) setConvertSlow(true);
    }, CONVERT_SLOW_MS);
    const refSel = refScenarios.find(x => x.cd === refScenarioCd);
    scenarioService.customGenerate(targetModule, {
      situation: contextText.trim(),
      referenceScenario: refSel
        ? { scenario_cd: refSel.cd, scenario_name: refSel.name, desc_txt: refSel.desc }
        : null,
    })
      .then(data => {
        if (seq !== convertSeqRef.current) return;   // 재변환 등으로 무효해진 응답은 폐기
        if (!mountedRef.current) {
          // 화면을 떠난 뒤 도착 — 저장된 작업상태에 실어두면 돌아왔을 때 선택할 수 있다.
          //   ⚠️ 이 시점의 정본은 redux 다. 이 인스턴스의 마지막 스냅샷(lastWorkRef)으로 통째로
          //   덮어쓰면, 사용자가 이미 돌아와 새 인스턴스에서 작업 중일 때 그 작업을 되돌려버린다.
          const cur = reduxUtil.getViewState(viewName)?.csmWork ?? lastWorkRef.current;
          if (cur) {
            dispatch(reduxUtil.updateViewState({
              viewName, csmWork: { ...cur, pendingAi: data ?? {} },
            }));
          }
          return;
        }
        if (manualModeRef.current) {
          setPendingAi(data ?? {});   // 수동 입력 중 — 덮어쓰지 않고 선택지로 제시
          return;
        }
        setEditingCd(""); applyAiResult(data ?? {}); setView("review");
      })
      .catch(err => {
        if (!mountedRef.current || seq !== convertSeqRef.current) return;
        const msg = err?.response?.data?.detail || err?.message || t("convertError");
        setConvertError(typeof msg === "string" ? msg : t("convertError"));
      })
      .finally(() => {
        clearTimeout(slowTimer);
        if (mountedRef.current && seq === convertSeqRef.current) {
          setConverting(false); setConvertSlow(false);
        }
      });
  };

  // "수동 입력으로 전환" — 변환 응답을 기다리지 않고 빈 검토 폼에서 직접 작성.
  //   진행 중인 요청은 그대로 두고(취소 X), 도착하면 선택 배너로 알린다.
  const switchToManual = () => {
    setManualMode(true); setConvertSlow(false); setEditingCd("");
    setScnName(""); setConfName(""); setDirection(""); setConfDirection("");
    setDescTxt(""); setConfDesc(""); setIssueSituation(""); setConfIssue("");
    setVariantDesc(""); setConfVariant(""); setTriggerKeywords([]); setConfTrigger("");
    setParamRows([]); setEvidences({}); setSrcText(""); setActiveEvidence("");
    // 파라미터 추가 후보·적용 범위는 변환 응답에서 오지만, 목록 조회분(모듈 카탈로그)으로 대체한다.
    setParamCatalog(customCatalogs[targetModule] ?? []);
    const scOpts = customScopeOptions[targetModule];
    setScopeType("all");
    setScopeOptions(Array.isArray(scOpts) && scOpts.length ? scOpts : [{ value: "all", label: "전체", label_key: "all" }]);
    setView("review");
  };

  // 도착한 AI 결과 적용 — 수동으로 채운 값을 덮어쓴다(사용자가 명시적으로 선택했을 때만).
  const acceptPendingAi = () => {
    if (!pendingAi) return;
    applyAiResult(pendingAi);
    setPendingAi(null); setManualMode(false);
  };

  // "확정 & 즉시 등록" — 검토 폼을 z_ai_scenario(source_type='USER')에 저장 후 완료 화면
  const handleSaveConfirm = () => {
    if (saving || !scnName.trim()) return;
    setSaving(true); setSaveError("");
    scenarioService.saveCustomScenario(targetModule, {
      scenarioCd:        editingCd,   // 빈 값 = 신규 등록, 있으면 해당 USER 행 수정
      scenarioName:      scnName.trim(),
      scenarioDirection: direction.trim(),
      descTxt:           descTxt.trim(),
      issueSituation:    issueSituation.trim(),
      triggerKeywords:   triggerKeywords,
      variantDesc:       variantDesc.trim(),
      scopeType:         scopeType,
      // 파라미터는 {key: value} 평탄 dict — z_ai_scenario.params(JSONB)
      params: Object.fromEntries(
        paramRows.filter(r => r.value !== "" && r.value != null
          && !(Array.isArray(r.value) && r.value.length === 0)).map(r => [r.key, r.value])),
    })
      .then(res => {
        if (!mountedRef.current) return;
        setSavedCd(res?.scenario_cd ?? "");
        // 서버가 허용 범위 밖 값을 접었으면 무엇을 어떻게 보정했는지 완료 화면에 알린다.
        setSaveWarnings(Array.isArray(res?.warnings) ? res.warnings : []);
        setEditingCd("");
        setManualMode(false); setPendingAi(null);
        loadCustomList();
        setView("done");
      })
      .catch(err => {
        if (!mountedRef.current) return;
        const msg = err?.response?.data?.detail || err?.message || t("customSaveError");
        setSaveError(typeof msg === "string" ? msg : t("customSaveError"));
      })
      .finally(() => { if (mountedRef.current) setSaving(false); });
  };

  // 삭제 모달 확정 — source_type='USER' 행만 삭제 가능(백엔드 가드)
  const handleDeleteConfirm = () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    scenarioService.deleteCustomScenario(deleteTarget.module_cd, deleteTarget.scenario_cd)
      .then(() => { if (mountedRef.current) loadCustomList(); })
      .catch(() => {})
      .finally(() => {
        if (!mountedRef.current) return;
        setDeleting(false); setDeleteTarget(null);
      });
  };

  // ── 공통 헤더 ── 좌: 타이틀 / 우: 네비 버튼
  //   위저드 뷰(등록 중)에서만 "시나리오 목록으로". 출처 화면이 있을 때만 "XX 플래닝으로 돌아가기".
  const headerEl = (
    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5, mb: 2 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: FONT.TITLE, fontWeight: 700, color: C.text1 }}>
          {t("customScenarioTitle")}
        </Typography>
        <Typography sx={{ fontSize: FONT.SUB, color: C.text3 }}>
          {t("customScenarioSubtitle")}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
        {view !== "list" && (
          <Button variant="outlined" size="small"
            startIcon={<FormatListBulletedIcon sx={{ fontSize: 14 }} />}
            onClick={() => setView("list")} sx={btnOutSx}>
            {t("backToListBtn")}
          </Button>
        )}
        {canGoBack && (
          <Button variant="outlined" size="small"
            startIcon={<ArrowBackIcon sx={{ fontSize: 14 }} />}
            onClick={goBack} sx={btnOutSx}>
            {t("backToPlanningBtn", { module: entryModule ?? targetModule })}
          </Button>
        )}
      </Box>
    </Box>
  );

  // ── 뷰: 목록 ──
  const listView = (
    <>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <ModuleChips value={listFilter} onChange={setListFilter} allLabel={t("moduleFilterAll")} />
        <Button variant="contained" size="small" startIcon={<AddCircleOutlineIcon sx={{ fontSize: 15 }} />}
          onClick={startRegister} sx={btnAccSx}>
          {t("newScenarioRegisterBtn")}
        </Button>
      </Box>
      {(() => {
        if (customLoading) {
          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {[0, 1, 2].map(i => (
                <Skeleton key={i} variant="rounded" height={58} sx={{ borderRadius: 1.5 }} />
              ))}
            </Box>
          );
        }
        const rows = listFilter ? customList.filter(r => r.module_cd === listFilter) : customList;
        if (!rows.length) {
          return (
            <Paper variant="outlined" sx={{
              borderColor: C.border, borderRadius: 2, py: 6,
              display: "flex", flexDirection: "column", alignItems: "center", gap: 1,
            }}>
              <InboxOutlinedIcon sx={{ fontSize: 34, color: C.text3 }} />
              <Typography sx={{ fontSize: FONT.BODY, fontWeight: 600, color: C.text2 }}>
                {t("scenarioListEmpty")}
              </Typography>
              <Typography sx={{ fontSize: FONT.SUB, color: C.text3 }}>
                {t("scenarioListEmptyHint")}
              </Typography>
            </Paper>
          );
        }
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {rows.map(row => {
              // 등록·수정 이력은 설명과 같은 줄에 이어 붙이면 어디까지가 설명인지 읽히지 않는다.
              //   행 우측(수정 버튼 왼쪽)에 별도 열로 두고, 등록/수정을 각각 한 줄씩 쌓는다.
              const day = (ts) => String(ts || "").slice(0, 10);
              // 모듈 색 — 좌측 뱃지·행 액센트 바가 같은 계열을 쓰도록 한 번만 조회
              const m = modC(row.module_cd);
              const createdLine = row.created_ts
                ? (row.created_by_name
                    ? t("registeredByAtLabel", { name: row.created_by_name, date: day(row.created_ts) })
                    : t("registeredAtLabel", { date: day(row.created_ts) }))
                : "";
              // 수정 줄은 등록 이후 실제로 고친 경우에만 (등록 직후엔 두 시각이 같다)
              const updatedLine = (row.updated_ts && String(row.updated_ts) !== String(row.created_ts))
                ? (row.updated_by_name
                    ? t("updatedByAtLabel", { name: row.updated_by_name, date: day(row.updated_ts) })
                    : t("updatedAtLabel", { date: day(row.updated_ts) }))
                : "";
              return (
              <Paper key={row.scenario_cd} variant="outlined" sx={{
                borderColor: C.border,
                // borderColor 로 4면을 칠한 뒤 좌측만 모듈 색으로 덮는다(순서 의존).
                borderLeft: `3px solid ${m.bar}`,
                borderRadius: 1.5, p: "10px 14px",
                display: "flex", alignItems: "center", gap: 1.5,
              }}>
                <Box sx={{
                  width: 34, height: 34, flexShrink: 0, borderRadius: "7px",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "11.5px", fontWeight: 800, letterSpacing: "0.02em",
                  bgcolor: m.bg, color: m.text, border: `1px solid ${m.border}`,
                }}>{row.module_cd}</Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: FONT.SUB, fontWeight: 600, color: C.text1 }}>
                      {row.scenario_name}
                    </Typography>
                    {/* CUSTOM 칩 없음 — 이 화면의 모든 행이 커스텀 시나리오라 구분값이 되지 못한다.
                        (시나리오 구성 탭에서는 표준과 섞이므로 거기서만 CUSTOM 칩을 붙인다.) */}
                    {/* 엔진 실행에 쓰인 횟수 — 삭제 판단(이 시나리오가 실제로 쓰이고 있는가)의 근거 */}
                    {row.usage_count > 0 && (
                      <Chip label={t("usageCountChip", { n: row.usage_count })} size="small"
                        sx={{ bgcolor: C.bg3, color: C.text2, fontSize: FONT.CAPTION, fontWeight: 600,
                              height: 20, flexShrink: 0, "& .MuiChip-label": { px: "6px" } }} />
                    )}
                  </Box>
                  <Typography noWrap sx={{ fontSize: FONT.BODY, color: C.text3, mt: "2px" }}>
                    {row.scenario_direction || row.desc_txt}
                  </Typography>
                </Box>
                {/* 등록·수정 이력 — 수정 버튼 왼쪽 전용 열. 수정 이력이 없으면 한 줄만 남는다. */}
                {(createdLine || updatedLine) && (
                  <Box sx={{ flexShrink: 0, textAlign: "right", display: "flex",
                             flexDirection: "column", gap: "1px" }}>
                    {createdLine && (
                      <Typography noWrap sx={{ fontSize: FONT.BODY, color: C.text3, lineHeight: 1.5 }}>
                        {createdLine}
                      </Typography>
                    )}
                    {updatedLine && (
                      <Typography noWrap sx={{ fontSize: FONT.BODY, color: C.text3, lineHeight: 1.5 }}>
                        {updatedLine}
                      </Typography>
                    )}
                  </Box>
                )}
                {/* 즐겨찾기 — 수정·삭제 앞에 둔다(기존 두 버튼 위치를 옮기지 않기 위해).
                    행 Paper 에 onClick 이 없어 stopPropagation 은 불필요. */}
                <Tooltip title={t(favCds.has(row.scenario_cd) ? "unfavoriteScenarioTooltip" : "favoriteScenarioTooltip")}>
                  <IconButton size="small" onClick={() => toggleFavorite(row)}
                    sx={{ color: favCds.has(row.scenario_cd) ? C.g2s : C.text3,
                          border: `1px solid ${C.border}`, borderRadius: 1.5,
                          width: 28, height: 28, "&:hover": { color: C.g2, bgcolor: C.g2l } }}>
                    {favCds.has(row.scenario_cd)
                      ? <StarIcon sx={{ fontSize: 15 }} />
                      : <StarBorderIcon sx={{ fontSize: 15 }} />}
                  </IconButton>
                </Tooltip>
                <IconButton size="small" onClick={() => handleEdit(row)}
                  sx={{ color: C.text3, border: `1px solid ${C.border}`, borderRadius: 1.5,
                        width: 28, height: 28, "&:hover": { color: C.accT, bgcolor: C.accL } }}>
                  <EditOutlinedIcon sx={{ fontSize: 15 }} />
                </IconButton>
                <IconButton size="small" onClick={() => setDeleteTarget(row)}
                  sx={{ color: C.text3, border: `1px solid ${C.border}`, borderRadius: 1.5,
                        width: 28, height: 28, "&:hover": { color: C.g3t, bgcolor: C.g3l } }}>
                  <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Paper>
              );
            })}
          </Box>
        );
      })()}
    </>
  );

  // ── 뷰: Context 입력 ──
  const contextView = (
    <>
      <StepIndicator current={0} />
      <Paper variant="outlined" sx={{ borderColor: C.border, borderRadius: 2, p: 2.5 }}>
        <Box sx={{ mb: 2 }}>
          <FieldLabel required>{t("targetModuleLabel")}</FieldLabel>
          <ModuleChips value={targetModule} onChange={setTargetModule} />
        </Box>
        <Box sx={{ mb: 2 }}>
          <FieldLabel required>{t("situationDescLabel")}</FieldLabel>
          <TextField fullWidth multiline rows={5} size="small"
            value={contextText} onChange={e => setContextText(e.target.value)}
            placeholder={t("situationDescPlaceholder")}
            sx={{ "& .MuiInputBase-root": { fontSize: FONT.BODY } }} />
          <Typography sx={{ fontSize: FONT.BODY, mt: 0.5, color: contextOk ? C.text3 : C.g2t }}>
            {t("situationDescCounter", { min: MIN_CONTEXT_LEN, n: contextText.trim().length })}
          </Typography>
        </Box>
        <Box>
          <FieldLabel>{t("refScenarioLabel")}</FieldLabel>
          {refLoading ? (
            <Skeleton variant="rounded" height={37} sx={{ borderRadius: 1.5 }} />
          ) : refScenarios.length > 0 ? (
            <>
              <Select value={refScenarioCd} onChange={e => setRefScenarioCd(e.target.value)}
                size="small" fullWidth displayEmpty
                renderValue={(sel) => {
                  const s = refScenarios.find(x => x.cd === sel);
                  return (
                    <Typography sx={{ fontSize: FONT.BODY, color: s ? C.text1 : C.text3 }}>
                      {s ? refName(s) : t("refScenarioHint")}
                    </Typography>
                  );
                }}
                sx={{ fontSize: FONT.BODY, "& .MuiSelect-select": { py: "8px" } }}>
                <MenuItem value="" sx={{ fontSize: FONT.BODY, color: C.text3 }}>
                  {t("refScenarioNone")}
                </MenuItem>
                {refScenarios.map(s => (
                  <MenuItem key={s.cd} value={s.cd} sx={{ display: "block", py: 1 }}>
                    <Typography sx={{ fontSize: FONT.BODY, fontWeight: 600, color: C.text1 }}>
                      {refName(s)}
                    </Typography>
                    {s.desc && (
                      <Typography sx={{ fontSize: FONT.BODY, color: C.text3, whiteSpace: "normal", mt: "2px" }}>
                        {refDesc(s)}
                      </Typography>
                    )}
                  </MenuItem>
                ))}
              </Select>
              {/* 선택한 참고 시나리오 설명 — 접힌 Select엔 이름만 보이므로 하단에 노출 */}
              {(() => {
                const sel = refScenarios.find(x => x.cd === refScenarioCd);
                return sel?.desc ? (
                  <Box sx={{ mt: 0.8, p: "8px 11px", borderRadius: 1.5, bgcolor: C.bg3,
                             border: `1px solid ${C.border}`, fontSize: FONT.BODY, color: C.text2, lineHeight: 1.6 }}>
                    {refDesc(sel)}
                  </Box>
                ) : null;
              })()}
            </>
          ) : (
            <Box sx={{
              p: "8px 11px", borderRadius: 1.5, border: `1px dashed ${C.border}`,
              fontSize: FONT.BODY, color: C.text3,
            }}>{t("refScenarioHint")}</Box>
          )}
        </Box>
      </Paper>
      {convertError && (
        <Box sx={{ mt: 1.5, p: "10px 14px", borderRadius: 1.5, bgcolor: C.g3l,
                   fontSize: FONT.SUB, color: C.g3t }}>
          {convertError}
        </Box>
      )}
      {/* 변환 지연 — 기다릴지 직접 쓸지 사용자가 고르게 한다(요청은 계속 진행) */}
      {converting && convertSlow && (
        <Box sx={{ mt: 1.5, p: "10px 14px", borderRadius: 1.5, bgcolor: C.g2l,
                   display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <WarningAmberIcon sx={{ fontSize: 16, color: C.g2t, flexShrink: 0 }} />
            <Typography sx={{ fontSize: FONT.SUB, color: C.g2t }}>
              {t("convertSlowHint")}
            </Typography>
          </Box>
          <Button variant="outlined" size="small" onClick={switchToManual}
            sx={{ ...btnOutSx, flexShrink: 0, borderColor: C.g2t, color: C.g2t,
                  "&:hover": { borderColor: C.g2t, color: C.g2t, bgcolor: "#FFFFFF80" } }}>
            {t("switchManualBtn")}
          </Button>
        </Box>
      )}
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
        <Button variant="outlined" size="small" startIcon={<ArrowBackIcon sx={{ fontSize: 13 }} />}
          onClick={() => setView("list")} sx={btnOutSx} disabled={converting}>
          {t("goListBtn")}
        </Button>
        <Button variant="contained" size="small"
          endIcon={converting
            ? <CircularProgress size={13} sx={{ color: "#fff" }} />
            : <ArrowForwardIcon sx={{ fontSize: 13 }} />}
          disabled={!contextOk || converting} onClick={handleConvert} sx={btnAccSx}>
          {converting ? t("convertingLabel") : t("nextConvertBtn")}
        </Button>
      </Box>
    </>
  );

  // 원문 위에 칠할 근거 구절 전체 (서술 필드 + 파라미터)
  const allEvidences = [
    evidences.name, evidences.direction, evidences.desc,
    evidences.issue, evidences.trigger, evidences.variant,
    ...paramRows.map(r => r.evidence),
  ].filter(Boolean);
  // 필드 포커스 시 그 필드의 근거만 진하게 — 어떤 문장에서 나온 값인지 즉시 연결
  const evFocus = (ev) => ({
    onFocus: () => setActiveEvidence(ev || ""),
    onBlur: () => setActiveEvidence(""),
  });

  // 신뢰도 집계 — 서술 6필드 + 파라미터 행. 렌더 중 계산(약 10개, memo 불필요).
  const confCounts = countConfidences([
    confName, confDirection, confDesc, confIssue, confTrigger, confVariant,
    ...paramRows.map(r => r.confidence),
  ]);
  // 좌열 구성 — 둘 다 없으면 열 자체를 접고 우열이 100% 를 쓴다(수정 모드·수동 입력).
  const showSrc  = !editingCd && srcText && allEvidences.length > 0;
  const showConf = !editingCd && !manualMode;
  const showLeft = !!(showSrc || showConf);
  // 필터는 "켤 수 있었던 상황"에서만 효력을 갖는다 — 필터를 켠 채 목록 → ✎ 수정으로 넘어가면
  //   신뢰도가 전부 빈 값이라 토글 버튼도 사라지고 화면이 텅 빈다. 파생 가드로 막는다.
  const lowOnlyOn = lowOnly && showConf && confCounts.low > 0;
  // 필터 중 남는 파라미터가 없으면 표 헤더만 덩그러니 남으므로 섹션째 감춘다.
  const lowParamCount = paramRows.filter(r => r.confidence === "low").length;
  const showParamSection = !lowOnlyOn || lowParamCount > 0;
  // 적용 범위는 신뢰도 개념이 없는 설정값 → 필터 중에는 감춘다(변형 효과가 낮음이면 전폭).
  const showScopeSelect = scopeOptions.length > 1 && !lowOnlyOn;

  // ── 뷰: 변환 검토 ──
  const reviewView = (
    <>
      <StepIndicator current={1} />
      {/* 수동 입력 중 백그라운드 변환 진행 안내 */}
      {manualMode && converting && !pendingAi && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: "10px 14px", mb: 1.5,
                   borderRadius: 1.5, bgcolor: C.accL }}>
          <CircularProgress size={13} sx={{ color: C.accT }} />
          <Typography sx={{ fontSize: FONT.SUB, color: C.accT }}>
            {t("aiConvertingBgBanner")}
          </Typography>
        </Box>
      )}
      {/* 변환 결과 도착 — 수동으로 쓴 내용을 덮어쓸지 사용자가 결정 */}
      {pendingAi && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5,
                   p: "10px 14px", mb: 1.5, borderRadius: 1.5, bgcolor: C.g1l }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <CheckCircleIcon sx={{ fontSize: 16, color: C.g1, flexShrink: 0 }} />
            <Typography sx={{ fontSize: FONT.SUB, color: C.g1t }}>
              {t("aiArrivedBanner")}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
            <Button variant="outlined" size="small" onClick={() => setPendingAi(null)} sx={btnOutSx}>
              {t("keepMyInputBtn")}
            </Button>
            <Button variant="contained" size="small" onClick={acceptPendingAi} sx={btnAccSx}>
              {t("useAiResultBtn")}
            </Button>
          </Box>
        </Box>
      )}
      {/* 본문 2열 — 컨테이너 쿼리 기준점. 컨테이너는 자기 쿼리에 매치하지 못하므로 래퍼가 한 겹 필요하다. */}
      <Box sx={{ containerType: "inline-size" }}>
        <Box sx={{ display: "grid", gap: 2, alignItems: "start",
                   gridTemplateColumns: showLeft ? "404px 1fr" : "1fr",
                   [RV_NARROW]: { gridTemplateColumns: "1fr" } }}>
          {/* 좌열(읽기 전용) — 스크롤해도 근거가 항상 보이도록 고정. 좁아지면 위로 올라가고 고정 해제. */}
          {showLeft && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, minWidth: 0,
                       position: "sticky", top: 16,
                       [RV_NARROW]: { position: "static", top: "auto" } }}>
              {/* 근거 하이라이트 — AI가 각 필드를 뽑을 때 인용한 상황 설명 부분 */}
              {showSrc && (
                <Paper variant="outlined" sx={{ borderColor: C.border, borderRadius: 2, overflow: "hidden" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: "11px 14px",
                             borderBottom: `1px solid ${ROW_RULE}` }}>
                    <Typography component="div" sx={{ fontSize: FONT.BODY, fontWeight: 700, color: C.text2 }}>
                      {t("situationSourceLabel")}
                    </Typography>
                    {/* 범례 — 원문 위 색칠이 무엇을 뜻하는지 */}
                    <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: "5px" }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: "2px", flexShrink: 0,
                                 bgcolor: C.accL, border: `1px solid ${C.accS}` }} />
                      <Typography component="div" sx={{ fontSize: FONT.CAPTION, color: C.text3 }}>
                        {t("evidenceLegendLabel")}
                      </Typography>
                    </Box>
                  </Box>
                  {/* 원문이 길면 카드가 화면을 넘겨 아래 신뢰도 카드가 밀려나므로 본문만 스크롤시킨다. */}
                  <Typography component="div" sx={{ p: "13px 14px", fontSize: FONT.BODY, color: C.text2,
                             lineHeight: 1.9, whiteSpace: "pre-wrap", wordBreak: "keep-all",
                             overflowWrap: "anywhere", maxHeight: "38vh", overflowY: "auto" }}>
                    <EvidenceText text={srcText} evidences={allEvidences} active={activeEvidence} />
                  </Typography>
                  <Typography component="div" sx={{ p: "0 14px 12px", fontSize: FONT.CAPTION, color: C.text3,
                             lineHeight: 1.6, wordBreak: "keep-all" }}>
                    {t("evidenceHighlightHint")}
                  </Typography>
                </Paper>
              )}
              {/* 추출 신뢰도 — 기존 검토 경고 배너를 흡수. AI 변환 직후에만(수정·수동 입력은 신뢰도 개념 없음) */}
              {showConf && (
                <Paper variant="outlined" sx={{ borderColor: C.border, borderRadius: 2, overflow: "hidden" }}>
                  <Typography component="div" sx={{ p: "11px 14px 9px", fontSize: FONT.BODY,
                             fontWeight: 700, color: C.text2 }}>
                    {t("confSummaryTitle")}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, p: "0 14px 12px" }}>
                    <ConfTile n={confCounts.high} label={t("confHighShort")} bg={C.g1l} color={C.g1t} />
                    <ConfTile n={confCounts.mid}  label={t("confMidShort")}  bg={C.g2l} color={C.g2t} />
                    <ConfTile n={confCounts.low}  label={t("confLowShort")}  bg={C.g3l} color={C.g3t} />
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, p: "11px 14px",
                             borderTop: `1px solid ${ROW_RULE}`, bgcolor: BTN.aiHoverBg }}>
                    <WarningAmberIcon sx={{ fontSize: 15, color: C.g2, flexShrink: 0, mt: "1px" }} />
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start",
                               gap: 1, minWidth: 0 }}>
                      <Typography component="div" sx={{ fontSize: FONT.BODY, color: C.g2t, lineHeight: 1.6,
                                 wordBreak: "keep-all", textWrap: "pretty" }}>
                        {t("reviewWarnBannerShort")}
                      </Typography>
                      {/* 낮음이 0건이면 볼 것이 없으므로 토글 자체를 숨긴다 */}
                      {confCounts.low > 0 && (
                        <Box component="button" type="button" onClick={() => setLowOnly(v => !v)}
                          aria-pressed={lowOnly}
                          sx={{ height: 24, px: "9px", borderRadius: "4px", cursor: "pointer",
                                border: `1px solid ${BTN.aiBorder}`,
                                bgcolor: lowOnly ? BTN.aiActiveBg : "#FBF1DE", color: C.g2t,
                                fontSize: FONT.CAPTION, fontWeight: 600, fontFamily: "inherit",
                                transition: "background-color .15s ease, border-color .15s ease",
                                "&:hover": { bgcolor: BTN.aiActiveBg, borderColor: BTN.aiHoverBorder },
                                "&:focus-visible": { outline: `2px solid ${C.acc}`, outlineOffset: "2px" } }}>
                          {/* 켜짐 상태에서는 "다시 누르면 전체로 돌아온다"를 라벨이 직접 말해 준다 */}
                          {lowOnly ? t("showAllConfItems") : t("showLowConfOnly", { n: confCounts.low })}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Paper>
              )}
            </Box>
          )}
          {/* 우열(편집) — overflow 를 주면 sticky 액션바가 죽으므로 모서리는 바 자체 radius 로 처리한다. */}
          <Paper variant="outlined" sx={{ borderColor: C.border, borderRadius: 2, minWidth: 0,
                     display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "14px", p: "16px 18px" }}>
              <SectionHead label={t("scenarioInfoSection")} />
              {/* 시나리오명·방향은 짧은 값이라 2열, 긴 문장을 넣는 필드는 단독 행 */}
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
                <Box sx={{ gridColumn: lowOnlyOn && confDirection !== "low" ? "1 / -1" : "auto" }}>
                  <FieldLabel required badge={<ConfBadge level={confName} />}>{t("scenarioNameLabel")}</FieldLabel>
                  <TextField fullWidth value={scnName} onChange={e => setScnName(e.target.value)}
                    {...evFocus(evidences.name)} sx={fieldSx(confName === "low" ? "low" : "base")} />
                  {confName === "low" && (
                    <Typography component="div" sx={{ mt: "5px", fontSize: FONT.CAPTION, color: C.g3t,
                               lineHeight: 1.5, wordBreak: "keep-all" }}>
                      {t("confLowFieldHint")}
                    </Typography>
                  )}
                </Box>
                {(!lowOnlyOn || confDirection === "low") && (
                  <Box>
                    <FieldLabel badge={<ConfBadge level={confDirection} />}>{t("scenarioDirectionLabel")}</FieldLabel>
                    <TextField fullWidth value={direction} onChange={e => setDirection(e.target.value)}
                      {...evFocus(evidences.direction)} sx={fieldSx(confDirection === "low" ? "low" : "base")} />
                    {confDirection === "low" && (
                      <Typography component="div" sx={{ mt: "5px", fontSize: FONT.CAPTION, color: C.g3t,
                                 lineHeight: 1.5, wordBreak: "keep-all" }}>
                        {t("confLowFieldHint")}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
              {(!lowOnlyOn || confDesc === "low") && (
                <Box>
                  <FieldLabel badge={<ConfBadge level={confDesc} />}>{t("scenarioDescLabel")}</FieldLabel>
                  <TextField fullWidth multiline rows={2} value={descTxt}
                    onChange={e => setDescTxt(e.target.value)}
                    {...evFocus(evidences.desc)} sx={areaSx(confDesc === "low" ? "low" : "base")} />
                  {confDesc === "low" && (
                    <Typography component="div" sx={{ mt: "5px", fontSize: FONT.CAPTION, color: C.g3t,
                               lineHeight: 1.5, wordBreak: "keep-all" }}>
                      {t("confLowFieldHint")}
                    </Typography>
                  )}
                </Box>
              )}
              {(!lowOnlyOn || confIssue === "low") && (
                <Box>
                  <FieldLabel badge={<ConfBadge level={confIssue} />}>{t("triggerCondLabel")}</FieldLabel>
                  <TextField fullWidth multiline rows={2} value={issueSituation}
                    onChange={e => setIssueSituation(e.target.value)}
                    {...evFocus(evidences.issue)} sx={areaSx(confIssue === "low" ? "low" : "base")} />
                  {confIssue === "low" && (
                    <Typography component="div" sx={{ mt: "5px", fontSize: FONT.CAPTION, color: C.g3t,
                               lineHeight: 1.5, wordBreak: "keep-all" }}>
                      {t("confLowFieldHint")}
                    </Typography>
                  )}
                </Box>
              )}
              {(!lowOnlyOn || confTrigger === "low") && (
                <Box>
                  <FieldLabel badge={<ConfBadge level={confTrigger} />}>{t("triggerKeywordsLabel")}</FieldLabel>
                  {/* 칩과 추가 입력이 한 박스 안 — Enter 또는 포커스 아웃 시 추가(중복·공백 무시는 addKeyword) */}
                  <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px",
                             minHeight: 34, p: "5px 8px", bgcolor: "#fff",
                             border: `1px solid ${confTrigger === "low" ? TONE.low.line : IN_BORDER}`,
                             borderRadius: "6px",
                             "&:hover": { borderColor: confTrigger === "low" ? TONE.low.hover : TONE.base.hover },
                             "&:focus-within": {
                               borderColor: confTrigger === "low" ? TONE.low.focus : TONE.base.focus,
                               boxShadow: `0 0 0 3px ${confTrigger === "low" ? TONE.low.ring : TONE.base.ring}`,
                             } }}>
                    {triggerKeywords.map((k, i) => (
                      <Chip key={`${k}-${i}`} label={k} size="small" onDelete={() => removeKeyword(i)}
                        sx={{ height: 22, borderRadius: "4px", bgcolor: C.accL, color: C.accT,
                              fontSize: FONT.CAPTION, fontWeight: 500,
                              "& .MuiChip-label": { pl: "9px", pr: "4px" },
                              "& .MuiChip-deleteIcon": { width: 15, height: 15, m: 0, mr: "4px",
                                fontSize: 15, color: C.accT, borderRadius: "3px",
                                "&:hover": { color: C.accT, bgcolor: C.accS } } }} />
                    ))}
                    <InputBase value={kwInput} placeholder={t("addKeywordPlaceholder")}
                      onChange={e => setKwInput(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addKeyword(); } }}
                      onFocus={() => setActiveEvidence(evidences.trigger || "")}
                      onBlur={() => { addKeyword(); setActiveEvidence(""); }}
                      sx={{ flex: 1, minWidth: 110, height: 22, fontSize: FONT.BODY, color: C.text1,
                            "& .MuiInputBase-input": { p: "0 2px" },
                            "& .MuiInputBase-input::placeholder": { color: C.text3, opacity: 1 } }} />
                  </Box>
                  {confTrigger === "low" && (
                    <Typography component="div" sx={{ mt: "5px", fontSize: FONT.CAPTION, color: C.g3t,
                               lineHeight: 1.5, wordBreak: "keep-all" }}>
                      {t("confLowFieldHint")}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
            {/* 파라미터 — 헤더 행이 있는 4열 그리드. 행별 패딩·구분선이 필요해 행마다 그리드를 두지만,
                열 폭이 고정px + 1fr 이라 행 간 정렬은 그대로 맞는다. */}
            <Box sx={{ display: showParamSection ? "flex" : "none",
                       flexDirection: "column", gap: "10px", p: "4px 18px 16px" }}>
              <SectionHead label={t("scenarioParamsLabel")} count={paramRows.length || null} />
              {paramRows.length > 0 ? (
                <Box sx={{ border: `1px solid ${C.border}`, borderRadius: "8px", overflow: "hidden" }}>
                  <Box sx={{ display: "grid", gridTemplateColumns: PARAM_COLS, gap: "12px",
                             alignItems: "center", p: "8px 12px", bgcolor: BG_PANEL,
                             borderBottom: `1px solid ${C.border}`,
                             fontSize: FONT.CAPTION, fontWeight: 600, color: C.text3 }}>
                    <Box>{t("paramColLabel")}</Box>
                    <Box>{t("paramColValue")}</Box>
                    <Box>{t("paramColConf")}</Box>
                    <Box />
                  </Box>
                  {paramRows.map((row, idx) => {
                    // 필터는 filter() 가 아니라 렌더 스킵으로 — setVal·삭제가 idx 클로저를 쓰므로
                    //   인덱스가 밀리면 엉뚱한 행이 수정·삭제된다.
                    if (lowOnlyOn && row.confidence !== "low") return null;
                    const setVal = (val) => setParamRows(rows => rows.map((r, i) =>
                      i === idx ? { ...r, value: val } : r));
                    // select·toggle(Y/N)은 드롭다운. 현재 값이 옵션에 없으면(LLM 자유값) 텍스트 입력으로 폴백
                    const hasOpt = row.options?.some(o => String(o.value) === String(row.value));
                    const asSelect = (row.type === "select" || row.type === "toggle")
                      && row.options?.length && (hasOpt || !row.value);
                    // 퍼센트 표시 파라미터(카탈로그 display="percent"): 저장 소수↔화면 % 변환, 단위는 "%".
                    const isPct = row.percent === true;
                    const unitTxt = isPct ? "%" : (row.unit_key ? t(row.unit_key) : (row.unit || ""));
                    // 허용 범위 밖 입력은 **경고만** 하고 확정을 막지 않는다(사용자 의도일 수 있음).
                    //   저장 시 서버가 경계값으로 접고, 무엇을 접었는지 완료 화면에서 알린다.
                    //   판정 축은 저장값(소수) — min/max 도 저장값 단위다. 표시만 화면 단위(%)로 환산한다.
                    const num = Number(row.value);
                    const outOfRange = (row.min != null || row.max != null)
                      && row.value !== "" && row.value != null && Number.isFinite(num)
                      && ((row.min != null && num < row.min) || (row.max != null && num > row.max));
                    const fmtBound = (v) => (v == null ? "-" : (isPct ? `${decToPct(v)}%` : String(v)));
                    const labelTxt = t(row.label_key || row.label) || row.key;
                    const topAlign = outOfRange ? { alignSelf: "start" } : {};
                    return (
                      <Box key={row.key} sx={{ display: "grid", gridTemplateColumns: PARAM_COLS, gap: "12px",
                                 alignItems: "center", p: "9px 12px",
                                 borderBottom: `1px solid ${ROW_RULE}`,
                                 "&:last-of-type": { borderBottom: "none" } }}>
                        {/* 플래닝 화면과 동일 규칙: t(label_key||label), 없으면 key 폴백. 한 줄 유지. */}
                        <Typography component="div" title={labelTxt}
                          sx={{ fontSize: FONT.BODY, color: C.text2, whiteSpace: "nowrap",
                                overflow: "hidden", textOverflow: "ellipsis",
                                ...topAlign, ...(outOfRange ? { mt: "6px" } : {}) }}>
                          {labelTxt}
                        </Typography>
                        {/* 값 칸 — 단위를 고정 28px 열에 두면 행마다 단위 위치가 정렬된다.
                            범위 경고도 이 칸 안(입력 아래)에 넣어 그리드가 정렬을 책임지게 한다. */}
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 28px", gap: 1,
                                   alignItems: "center", minWidth: 0 }}>
                          {row.type === "custHrchy" ? (
                            // 거래처 계층 등 마스터 코드 선택형 — 시나리오 구성 화면과 동일 트리 셀렉터 재사용
                            <Box sx={{ minWidth: 0 }}>
                              <DpCustHrchySelect
                                value={Array.isArray(row.value) ? row.value : (row.value ? [row.value] : [])}
                                multi={row.multi !== false} accent={C.acc}
                                inputSx={hrchyInputSx(30)}
                                onChange={(codes) => setVal(codes)} />
                            </Box>
                          ) : asSelect ? (
                            <Select fullWidth value={row.value ?? ""}
                              onChange={e => setVal(e.target.value)}
                              {...evFocus(row.evidence)}
                              sx={selectSx(outOfRange ? "warn" : "base", 30)}>
                              {row.options.map(o => (
                                <MenuItem key={String(o.value)} value={o.value} sx={{ fontSize: FONT.BODY }}>
                                  {t(o.label_key || o.label) || String(o.value)}
                                </MenuItem>
                              ))}
                            </Select>
                          ) : (
                            <TextField fullWidth
                              value={isPct ? decToPct(row.value) : row.value}
                              onChange={e => setVal(isPct ? pctToDec(e.target.value) : e.target.value)}
                              {...evFocus(row.evidence)}
                              sx={{ ...fieldSx(outOfRange ? "warn" : "base", 30),
                                    "& .MuiOutlinedInput-input": {
                                      height: "100%", boxSizing: "border-box", p: "0 10px",
                                      fontVariantNumeric: "tabular-nums",
                                    } }} />
                          )}
                          <Typography component="div" sx={{ fontSize: FONT.CAPTION, color: C.text3 }}>
                            {unitTxt}
                          </Typography>
                          {outOfRange && (
                            <Typography component="div" sx={{ gridColumn: "1 / -1", fontSize: FONT.CAPTION,
                                       color: C.g2t, lineHeight: 1.5, wordBreak: "keep-all" }}>
                              {t("paramRangeWarnClamp", {
                                min: fmtBound(row.min), max: fmtBound(row.max),
                                clamp: fmtBound(clampPreview(row, num)),
                              })}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ justifySelf: "start", ...topAlign, ...(outOfRange ? { mt: "6px" } : {}) }}>
                          <ConfBadge level={row.confidence} short />
                        </Box>
                        {/* 행 삭제 — LLM이 잘못 뽑았거나 불필요한 파라미터 제거 */}
                        <IconButton size="small" aria-label={t("deleteParamAria")}
                          onClick={() => setParamRows(rows => rows.filter((_, i) => i !== idx))}
                          sx={{ color: C.text3, width: 26, height: 26, borderRadius: "5px", ...topAlign,
                                "&:hover": { color: C.g3t, bgcolor: C.g3l } }}>
                          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Box sx={{ p: "10px 12px", borderRadius: 1.5, bgcolor: C.bg3, border: `1px solid ${C.border}`,
                           fontSize: FONT.SUB, color: C.text3 }}>{t("noParamsGenerated")}</Box>
              )}
              {/* 파라미터 추가 — 모듈 카탈로그 중 아직 없는 항목만 후보로 노출 */}
              <AddParamButton
                candidates={(() => {
                  const usedKeys = new Set(paramRows.map(r => r.key));
                  return paramCatalog.filter(c => c?.key && !usedKeys.has(c.key));
                })()}
                onPick={(m) => setParamRows(rows => [...rows, {
                  key: m.key, value: "", confidence: "", evidence: "",
                  label: m.label ?? "", label_key: m.label_key ?? null,
                  unit: m.unit ?? "", unit_key: m.unit_key ?? null,
                  type: m.type ?? "text", options: Array.isArray(m.options) ? m.options : null, multi: m.multi,
                  min: m.min ?? null, max: m.max ?? null,
                  percent: m.display === "percent",   // 소수↔% 표시 변환 대상
                }])} />
            </Box>
            {/* 적용 범위 · 변형 효과 — scope 를 지원하지 않는 모듈이면 변형 효과가 전폭을 쓴다 */}
            {(!lowOnlyOn || confVariant === "low") && (
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", p: "4px 18px 18px" }}>
                {showScopeSelect && (
                  <Box>
                    <FieldLabel>{t("scopeTypeLabel")}</FieldLabel>
                    <Select value={scopeType} onChange={e => setScopeType(e.target.value)} fullWidth
                      sx={selectSx("base", 34)}>
                      {scopeOptions.map(o => (
                        <MenuItem key={o.value} value={o.value} sx={{ fontSize: FONT.BODY }}>{t(o.label_key || o.label)}</MenuItem>
                      ))}
                    </Select>
                    <Typography component="div" sx={{ mt: "5px", fontSize: FONT.CAPTION, color: C.text3,
                               lineHeight: 1.6, wordBreak: "keep-all" }}>
                      {t("scopeTypeHint")}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ gridColumn: showScopeSelect ? "auto" : "1 / -1" }}>
                  <FieldLabel badge={<ConfBadge level={confVariant} />}>{t("variantDescLabel")}</FieldLabel>
                  <TextField fullWidth multiline rows={2} value={variantDesc}
                    onChange={e => setVariantDesc(e.target.value)}
                    {...evFocus(evidences.variant)} sx={areaSx(confVariant === "low" ? "low" : "base")} />
                  {confVariant === "low" && (
                    <Typography component="div" sx={{ mt: "5px", fontSize: FONT.CAPTION, color: C.g3t,
                               lineHeight: 1.5, wordBreak: "keep-all" }}>
                      {t("confLowFieldHint")}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            {/* 액션 바 — 폼이 길어도 항상 손에 닿게 카드 안에서 하단 고정 */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2,
                       p: "13px 18px", borderTop: `1px solid ${C.border}`, bgcolor: BG_PANEL,
                       borderRadius: "0 0 8px 8px", position: "sticky", bottom: 0 }}>
              <Typography component="div" sx={{ flex: 1, minWidth: 0, fontSize: FONT.CAPTION,
                         color: saveError ? C.g3t : C.text3, lineHeight: 1.5, wordBreak: "keep-all",
                         display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                         overflow: "hidden" }}>
                {saveError || (showConf && confCounts.low > 0
                  ? t("lowConfRemainHint", { n: confCounts.low }) : "")}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                {/* 수정 모드는 Context 입력을 거치지 않고 진입 → 이전 = 목록 복귀 */}
                <Button variant="outlined" size="small" startIcon={<ArrowBackIcon sx={{ fontSize: 13 }} />}
                  onClick={() => setView(editingCd ? "list" : "context")} sx={btnOutSx} disabled={saving}>
                  {editingCd ? t("goListBtn") : t("prev")}
                </Button>
                <Button variant="contained" size="small"
                  endIcon={saving
                    ? <CircularProgress size={13} sx={{ color: "#fff" }} />
                    : <ArrowForwardIcon sx={{ fontSize: 13 }} />}
                  disabled={saving || !scnName.trim()} onClick={handleSaveConfirm} sx={btnAccSx}>
                  {t("confirmRegisterBtn")}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>
    </>
  );

  // ── 뷰: 등록 완료 ──
  const doneView = (
    <>
      <StepIndicator current={2} />
      <Paper variant="outlined" sx={{
        borderColor: C.border, borderRadius: 2, py: 5, px: 3,
        display: "flex", flexDirection: "column", alignItems: "center", gap: 1.2, textAlign: "center",
      }}>
        <Box sx={{
          width: 46, height: 46, borderRadius: "50%", bgcolor: C.g1l,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <CheckCircleIcon sx={{ fontSize: 26, color: C.g1 }} />
        </Box>
        <Typography sx={{ fontSize: FONT.TITLE, fontWeight: 700, color: C.text1 }}>
          {t("regDoneTitle")}
        </Typography>
        <Typography sx={{ fontSize: FONT.SUB, color: C.text2, maxWidth: 420 }}>
          {t("regDoneDesc", { name: scnName || t("scenarioNameLabel"), module: targetModule })}
        </Typography>
        {/* 등록 요약 카드 */}
        <Paper variant="outlined" sx={{
          borderColor: C.border, borderRadius: 1.5, p: "10px 14px", mt: 1,
          width: "100%", maxWidth: 420, textAlign: "left",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1,
        }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: FONT.BODY, fontWeight: 600, color: C.text1 }}>
              {scnName || "—"}
            </Typography>
            <Typography sx={{ fontSize: FONT.BODY, color: C.text3, mt: "2px" }}>
              {[targetModule, savedCd].filter(Boolean).join(" · ")}
            </Typography>
          </Box>
          <Chip label={t("statusActiveLabel")} size="small"
            sx={{ bgcolor: C.g1l, color: C.g1t, fontSize: FONT.MICRO, fontWeight: 700,
                  height: 20, "& .MuiChip-label": { px: "8px" } }} />
        </Paper>
        {/* 범위 보정 안내 — 저장 시 서버가 허용 범위로 접은 파라미터가 있으면 무엇을 바꿨는지 알린다 */}
        {saveWarnings.length > 0 && (
          <Box sx={{ width: "100%", maxWidth: 420, textAlign: "left", mt: 0.5,
                     p: "10px 12px", borderRadius: 1.5, bgcolor: C.g2l }}>
            <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: C.g2t, mb: 0.5 }}>
              {t("paramClampedNotice")}
            </Typography>
            {saveWarnings.map(wn => (
              <Typography key={wn.key} sx={{ fontSize: FONT.CAPTION, color: C.g2t }}>
                {t("paramClampedItem", {
                  label: t(wn.label_key || wn.label) || wn.key, from: wn.from, to: wn.to,
                })}
              </Typography>
            ))}
          </Box>
        )}
        <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
          <Button variant="outlined" size="small" onClick={() => setView("list")} sx={btnOutSx}>
            {t("goListBtn")}
          </Button>
          {canGoCompose && (
            <Button variant="contained" size="small" endIcon={<ArrowForwardIcon sx={{ fontSize: 13 }} />}
              onClick={goCompose} sx={btnAccSx}>
              {t("useInComposeBtn")}
            </Button>
          )}
        </Box>
      </Paper>
    </>
  );

  return (
    <Box sx={{ p: "16px 20px 40px", bgcolor: C.bg, height: "100%", overflow: "auto", boxSizing: "border-box" }}>
      {headerEl}
      {view === "list" && listView}
      {view === "context" && contextView}
      {view === "review" && reviewView}
      {view === "done" && doneView}

      {/* 삭제 확인 모달 */}
      <Modal open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)}>
        <Box sx={{
          position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
          width: 420, bgcolor: "#fff", borderRadius: "12px", p: 3, boxShadow: 24,
        }}>
          <Typography sx={{ fontSize: FONT.TITLE, fontWeight: 700, color: C.text1, mb: 1 }}>
            {t("deleteScenarioModalTitle")}
          </Typography>
          <Typography sx={{ fontSize: FONT.SUB, color: C.text2, lineHeight: 1.6, mb: 2 }}>
            {deleteTarget?.scenario_name
              ? t("deleteScenarioModalDescNamed", { name: deleteTarget.scenario_name })
              : t("deleteScenarioModalDesc")}
          </Typography>
          {/* 실제로 계획에 쓰인 시나리오는 삭제 영향이 크다 — 사용 횟수를 명시해 경고 */}
          {deleteTarget?.usage_count > 0 && (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 2,
                       p: "10px 12px", borderRadius: 1.5, bgcolor: C.g2l }}>
              <WarningAmberIcon sx={{ fontSize: 16, color: C.g2t, flexShrink: 0, mt: "1px" }} />
              <Typography sx={{ fontSize: FONT.SUB, color: C.g2t, lineHeight: 1.6 }}>
                {t("deleteScenarioModalDescUsed", { n: deleteTarget.usage_count })}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button variant="outlined" size="small" onClick={() => setDeleteTarget(null)}
              sx={btnOutSx} disabled={deleting}>
              {t("cancel")}
            </Button>
            <Button variant="contained" size="small" onClick={handleDeleteConfirm}
              startIcon={deleting ? <CircularProgress size={13} sx={{ color: "#fff" }} /> : null}
              disabled={deleting}
              sx={{ ...btnAccSx, bgcolor: C.g3, "&:hover": { bgcolor: "#c93d3c" } }}>
              {t("delete")}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
